import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { of, switchMap } from 'rxjs';

export const AuthGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const router: Router = inject(Router);
    const authService = inject(AuthService);
    const routePath = route.routeConfig?.path || '';
    const embedParam = route.queryParamMap.get('embed');
    const normalizedUrl = (state.url || '').toLowerCase();
    const isEmbedRoute = routePath.startsWith('embed')
        || routePath.startsWith('jedstion-embed')
        || normalizedUrl.includes('/embed')
        || normalizedUrl.includes('/jedstion-embed')
        || normalizedUrl.includes('embed=1')
        || embedParam === '1';

    if (isEmbedRoute) {
        let urlToken = route.queryParamMap.get('token');
        let hasTokenParam = route.queryParamMap.has('token');

        if (!hasTokenParam && typeof window !== 'undefined') {
            const searchParams = new URLSearchParams(window.location.search || '');
            if (searchParams.has('token')) {
                hasTokenParam = true;
                urlToken = searchParams.get('token');
            }

            if (!hasTokenParam) {
                const hash = window.location.hash || '';
                const queryIndex = hash.indexOf('?');
                if (queryIndex >= 0) {
                    const hashQuery = hash.slice(queryIndex + 1);
                    const hashParams = new URLSearchParams(hashQuery);
                    if (hashParams.has('token')) {
                        hasTokenParam = true;
                        urlToken = hashParams.get('token');
                    }
                }
            }
        }

        if (hasTokenParam && !authService.applyExternalToken(urlToken, { embedded: true })) {
            authService.clearEmbedToken();

            if (!authService.accessToken) {
                return of(router.createUrlTree(['/sign-in'], {
                    queryParams: state.url === '/sign-out' ? undefined : { redirectURL: state.url },
                }));
            }
        }
    } else {
        authService.clearEmbedToken();
    }

    // Check the authentication status
    return authService
        .check()
        .pipe(
            switchMap((authenticated) => {
                // If the user is not authenticated...
                if (!authenticated) {
                    // Redirect to the sign-in page with a redirectUrl param
                    const urlTree = router.createUrlTree(['/sign-in'], {
                        queryParams: state.url === '/sign-out' ? undefined : { redirectURL: state.url },
                    });
                    return of(urlTree);
                } else {
                    // Allow the access
                    return of(true);
                }
            }),
        );
};
